import type {
  ButtonHTMLAttributes,
  ComponentProps,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import {
  IconoAlerta,
  IconoAnadir,
  IconoAtras,
  IconoPunta,
  IconoSinConexion,
  IconoVacio,
} from './iconos.js';
import './ui.css';

type Variante = 'primario' | 'secundario' | 'texto' | 'peligro';

export interface BotonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  ancho?: 'auto' | 'completo';
  /**
   * La accion esta en curso.
   *
   * Hasta el 08/09/2026 esto se fingia en nueve sitios cambiando la etiqueta a
   * «Guardando…» y poniendo `disabled`: el boton se quedaba gris y quieto, que
   * es exactamente lo que se ve cuando una accion NO se puede pulsar. El §11
   * pide que el usuario sepa que su pulsacion ha sido recibida, y eso es un
   * estado propio, no la ausencia de uno.
   *
   * Bloquea la pulsacion igual que `disabled` —no se puede guardar dos veces—
   * pero conserva el color de la accion y anuncia `aria-busy`.
   */
  cargando?: boolean;
}

const VARIANTE_CLASE: Record<Variante, string> = {
  primario: '',
  secundario: 'boton--secundario',
  texto: 'boton--texto',
  peligro: 'boton--peligro',
};

export function Boton({
  variante = 'primario',
  ancho = 'auto',
  cargando = false,
  className,
  type = 'button',
  disabled,
  children,
  ...rest
}: BotonProps): ReactNode {
  const clases = [
    'boton',
    VARIANTE_CLASE[variante],
    ancho === 'completo' ? 'boton--primario-grande' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type={type}
      className={clases}
      disabled={disabled === true || cargando}
      aria-busy={cargando}
      {...rest}
    >
      {cargando && <span className="boton__anillo" aria-hidden="true" />}
      {children}
    </button>
  );
}

/**
 * Un ENLACE que se ve como un boton.
 *
 * Habia 25 sitios escritos como `<Link to="…"><Boton>Ir</Boton></Link>`. Eso
 * es un `<button>` dentro de un `<a>`, que ademas de ser HTML invalido tiene
 * una consecuencia que se nota: son DOS elementos enfocables, asi que con el
 * teclado cada accion principal de la aplicacion se recorria dos veces, y el
 * lector de pantalla anunciaba un enlace y dentro un boton con el mismo
 * nombre. El §19 pide HTML semantico antes que parches.
 *
 * La regla, ahora que hay las dos piezas: si NAVEGA es `BotonEnlace`; si
 * EJECUTA algo, `Boton`.
 */
export function BotonEnlace({
  a,
  variante = 'primario',
  ancho = 'auto',
  className,
  children,
  ...rest
}: {
  a: string;
  variante?: Variante;
  ancho?: 'auto' | 'completo';
  className?: string | undefined;
  children: ReactNode;
} & Omit<ComponentProps<typeof Link>, 'to' | 'className'>): ReactNode {
  const clases = [
    'boton',
    VARIANTE_CLASE[variante],
    ancho === 'completo' ? 'boton--primario-grande' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Link to={a} className={clases} {...rest}>
      {children}
    </Link>
  );
}

/**
 * Cabecera de pantalla.
 *
 * Las 24 pantallas repetian `<header className="cabecera"><h1>…` a mano y
 * pegaban el subtitulo con `className="campo__pista"` —la clase de la PISTA DE
 * UN CAMPO DE FORMULARIO— en 14 sitios. El resultado no era feo: era
 * inconsistente, que es peor, porque cada pantalla decidia por su cuenta si
 * habia subtitulo, si habia vuelta atras y a que distancia empezaba el
 * contenido.
 *
 * `volver` no es decorativo: varias subpantallas de «Mas» no tenian ninguna
 * forma de retroceder que no fuera el gesto del sistema, que en la PWA de
 * escritorio no existe (§9).
 */
export function Cabecera({
  titulo,
  descripcion,
  encima,
  volver,
  acciones,
}: {
  titulo: string;
  descripcion?: string | undefined;
  /** Linea de contexto por ENCIMA del titulo: la fecha, el bloque, la semana. */
  encima?: ReactNode;
  volver?: { a: string; etiqueta: string } | undefined;
  acciones?: ReactNode;
}): ReactNode {
  return (
    <header className="cabecera">
      {volver !== undefined && (
        <Link to={volver.a} className="cabecera__volver">
          <IconoAtras />
          {volver.etiqueta}
        </Link>
      )}
      <div className="cabecera__fila">
        <div className="cabecera__texto">
          {encima !== undefined && <p className="cabecera__encima">{encima}</p>}
          <h1>{titulo}</h1>
          {descripcion !== undefined && <p className="cabecera__descripcion">{descripcion}</p>}
        </div>
        {acciones !== undefined && <div className="cabecera__acciones">{acciones}</div>}
      </div>
    </header>
  );
}

/**
 * Agrupacion SIN superficie: el contenedor por defecto.
 *
 * Antes solo existia `Tarjeta`, asi que agrupar dos cosas obligaba a elevarlas.
 * Con todo elevado, la elevacion deja de significar algo y la pantalla se
 * convierte en la lista de cajas que el §8 prohibe. Una seccion agrupa con lo
 * que deberia: un titulo, un filete y espacio.
 *
 * `rotulo` la baja de rango —el titulo pasa a versalita— para los bloques que
 * etiquetan contenido en vez de encabezarlo: ajustes, tablas, listas de
 * referencia.
 */
export function Seccion({
  titulo,
  descripcion,
  acciones,
  rotulo = false,
  children,
  className,
}: {
  titulo?: string | undefined;
  descripcion?: string | undefined;
  acciones?: ReactNode;
  rotulo?: boolean;
  children?: ReactNode;
  className?: string | undefined;
}): ReactNode {
  const clases = ['seccion', rotulo ? 'seccion--rotulo' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <section className={clases}>
      {/* La descripcion va DENTRO de la cabecera, encima del filete: debajo se
          quedaba flotando entre la regla y el contenido, sin pertenecer a
          ninguno de los dos. El filete separa el encabezado entero del cuerpo,
          y eso incluye lo que explica de que va la seccion. */}
      {(titulo !== undefined || acciones !== undefined || descripcion !== undefined) && (
        <header className="seccion__cabecera">
          <div className="seccion__fila">
            {titulo !== undefined && <h2 className="seccion__titulo">{titulo}</h2>}
            {acciones !== undefined && <div className="seccion__acciones">{acciones}</div>}
          </div>
          {descripcion !== undefined && <p className="seccion__descripcion">{descripcion}</p>}
        </header>
      )}
      {children !== undefined && <div className="seccion__cuerpo">{children}</div>}
    </section>
  );
}

export function Tarjeta({
  titulo,
  descripcion,
  children,
  acciones,
  tono = 'normal',
  className,
}: {
  // `| undefined` explicito: estos campos se REENVIAN desde quien llama, y con
  // `exactOptionalPropertyTypes` un `descripcion={algo?.texto}` no compila si
  // el tipo solo admite ausencia. El error sale en la llamada, no aqui.
  titulo?: string | undefined;
  descripcion?: string | undefined;
  children?: ReactNode;
  acciones?: ReactNode;
  /**
   * `focal` es la respuesta a «¿que es lo mas importante de esta pantalla?»
   * (§4). Como mucho UNA por pantalla; con dos deja de contestar la pregunta.
   */
  tono?: 'normal' | 'focal' | 'aviso' | 'peligro';
  className?: string | undefined;
}): ReactNode {
  const clases = [
    'tarjeta',
    tono === 'normal' ? '' : `tarjeta--${tono}`,
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <section className={clases}>
      {titulo !== undefined && <h2 className="tarjeta__titulo">{titulo}</h2>}
      {descripcion !== undefined && <p className="tarjeta__descripcion">{descripcion}</p>}
      {children}
      {acciones !== undefined && <div className="tarjeta__acciones">{acciones}</div>}
    </section>
  );
}

/**
 * Tarjeta que ES un destino de navegacion.
 *
 * `MasPage` envolvia una `<Tarjeta>` en un `<Link>` con `textDecoration: none`
 * en linea. Funcionaba, pero no tenia hover, ni estado activo, ni punta que
 * dijera que llevaba a algun sitio, y el anillo de foco rodeaba el enlace y no
 * la tarjeta. Diez filas identicas sin una sola señal de ser pulsables.
 */
export function TarjetaEnlace({
  a,
  titulo,
  descripcion,
}: {
  a: string;
  titulo: string;
  descripcion?: string | undefined;
}): ReactNode {
  return (
    <Link to={a} className="tarjeta-enlace">
      <span className="tarjeta-enlace__texto">
        <span className="tarjeta-enlace__titulo">{titulo}</span>
        {descripcion !== undefined && (
          <span className="tarjeta-enlace__descripcion">{descripcion}</span>
        )}
      </span>
      <IconoPunta className="tarjeta-enlace__punta" />
    </Link>
  );
}

export interface CampoTextoProps extends InputHTMLAttributes<HTMLInputElement> {
  etiqueta: string;
  pista?: string;
  error?: string;
  /**
   * La etiqueta sigue existiendo para quien no ve la pantalla, pero no se
   * pinta. Para cuando el nombre del campo YA esta escrito al lado: en el
   * detalle de una comida decia «Pan integral» a la izquierda y «Pan integral
   * (g)» encima del campo, dos copias del mismo texto partiendo la fila en
   * tres lineas. Ocultar no es quitar: el `<label>` sigue asociado.
   */
  etiquetaOculta?: boolean;
  /** Unidad pegada al campo («g», «kg», «cm»). Decorativa: la dice la etiqueta. */
  sufijo?: string;
}

export function CampoTexto({
  etiqueta,
  pista,
  error,
  etiquetaOculta = false,
  sufijo,
  id,
  ...rest
}: CampoTextoProps): ReactNode {
  const inputId = id ?? `campo-${etiqueta.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="campo">
      <label
        className={etiquetaOculta ? 'visualmente-oculto' : 'campo__etiqueta'}
        htmlFor={inputId}
      >
        {etiqueta}
      </label>
      <div className={sufijo === undefined ? undefined : 'campo__caja'}>
        <input
          id={inputId}
          className="campo__control"
          aria-describedby={pista !== undefined ? `${inputId}-pista` : undefined}
          aria-invalid={error !== undefined}
          {...rest}
        />
        {sufijo !== undefined && (
          <span className="campo__sufijo" aria-hidden="true">
            {sufijo}
          </span>
        )}
      </div>
      {pista !== undefined && (
        <span className="campo__pista" id={`${inputId}-pista`}>
          {pista}
        </span>
      )}
      {error !== undefined && (
        <span className="campo__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface CampoNumeroProps extends Omit<CampoTextoProps, 'type' | 'inputMode'> {
  decimales?: boolean;
}

/** Campo numerico con el teclado correcto en movil (requisito §74). */
export function CampoNumero({ decimales = false, ...rest }: CampoNumeroProps): ReactNode {
  return (
    <CampoTexto
      type="number"
      inputMode={decimales ? 'decimal' : 'numeric'}
      step={decimales ? '0.01' : '1'}
      {...rest}
    />
  );
}

export interface CampoSeleccionProps extends SelectHTMLAttributes<HTMLSelectElement> {
  etiqueta: string;
  opciones: { valor: string; texto: string }[];
  pista?: string;
}

export function CampoSeleccion({
  etiqueta,
  opciones,
  pista,
  id,
  ...rest
}: CampoSeleccionProps): ReactNode {
  const selectId = id ?? `sel-${etiqueta.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="campo">
      <label className="campo__etiqueta" htmlFor={selectId}>
        {etiqueta}
      </label>
      <select id={selectId} className="campo__control" {...rest}>
        {opciones.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.texto}
          </option>
        ))}
      </select>
      {pista !== undefined && <span className="campo__pista">{pista}</span>}
    </div>
  );
}

export function Chip({
  children,
  tono = 'verde',
}: {
  children: ReactNode;
  tono?: 'verde' | 'neutro' | 'aviso' | 'error';
}): ReactNode {
  const clase =
    tono === 'neutro'
      ? 'chip chip--neutro'
      : tono === 'aviso'
        ? 'chip chip--aviso'
        : tono === 'error'
          ? 'chip chip--error'
          : 'chip';
  return <span className={clase}>{children}</span>;
}

/**
 * Estados obligatorios de interfaz (requisito §72). Un estado vacio siempre
 * ofrece la accion siguiente, no solo un texto.
 *
 * Cada tipo trae su propia marca: los cinco se dibujaban igual, asi que «no hay
 * nada todavia» y «esto ha fallado» se leian con el mismo peso. Y `compacto`
 * existe porque dentro de una `Tarjeta` los 48 px de relleno se sumaban a los
 * de la tarjeta y el bloque parecia descuadrado.
 */
export type TipoEstado = 'vacio' | 'cargando' | 'error' | 'offline' | 'primera-vez';

const MARCA_DE_ESTADO: Record<Exclude<TipoEstado, 'cargando'>, () => ReactNode> = {
  vacio: () => <IconoVacio />,
  error: () => <IconoAlerta />,
  offline: () => <IconoSinConexion />,
  'primera-vez': () => <IconoAnadir />,
};

export function Estado({
  tipo,
  titulo,
  descripcion,
  accion,
  compacto = false,
  alineacion = 'centro',
}: {
  tipo: TipoEstado;
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
  compacto?: boolean;
  /**
   * `inicio` cuando el estado ES el contenido de algo que ya tiene título
   * alineado a la izquierda —el día de Plan—. Centrado debajo de ese título se
   * quedaba flotando en su columna (revisión de UX, 23/09/2026). Lo normal, un
   * hueco en una pantalla o una tarjeta, sigue centrado.
   */
  alineacion?: 'centro' | 'inicio';
}): ReactNode {
  if (tipo === 'cargando') {
    // El esqueleto tiene la FORMA de lo que viene detras —un titulo, dos
    // lineas y un bloque— para que la pantalla no salte al llegar los datos.
    return (
      <div className="cargando" role="status" aria-live="polite">
        <div className="esqueleto" style={{ height: 22, width: '52%' }} />
        <div className="esqueleto" style={{ height: 14, width: '86%' }} />
        <div className="esqueleto" style={{ height: 14, width: '64%' }} />
        <div className="esqueleto" style={{ height: 96, width: '100%' }} />
        <span className="visualmente-oculto">{titulo}</span>
      </div>
    );
  }

  const clases = [
    'estado',
    `estado--${tipo}`,
    compacto ? 'estado--compacto' : '',
    alineacion === 'inicio' ? 'estado--inicio' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={clases} role={tipo === 'error' ? 'alert' : undefined}>
      <span className="estado__marca" aria-hidden="true">
        {MARCA_DE_ESTADO[tipo]()}
      </span>
      {/*
        Encabezado y no párrafo (NEW-12, 12/09/2026). Con `<p>`, una pantalla
        que solo enseñaba su estado de primera vez no tenía NINGÚN encabezado:
        quien navega por encabezados con un lector de pantalla no encontraba
        nada, y el barrido de `smoke.spec.ts` pasaba gracias a los datos que le
        dejaban otros specs. El nivel sale de dónde vive: a pantalla completa
        cuelga del `h1` de la cabecera; compacto va dentro de una tarjeta o una
        sección, que ya son `h2`. El aspecto no cambia: ver `.estado__titulo`.
      */}
      {compacto ? (
        <h3 className="estado__titulo">{titulo}</h3>
      ) : (
        <h2 className="estado__titulo">{titulo}</h2>
      )}
      {descripcion !== undefined && <p className="estado__descripcion">{descripcion}</p>}
      {accion !== undefined && <div className="estado__accion">{accion}</div>}
    </div>
  );
}

export function ListaDatos({ filas }: { filas: { clave: string; valor: string }[] }): ReactNode {
  return (
    <dl className="lista-datos">
      {filas.map((fila) => (
        <div className="lista-datos__fila" key={fila.clave}>
          <dt className="lista-datos__clave">{fila.clave}</dt>
          <dd className="lista-datos__valor numeros">{fila.valor}</dd>
        </div>
      ))}
    </dl>
  );
}
